import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ApiService } from '../../../core/api-service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CampaignsActions, MediaPlatformActions } from './campaign-actions';
import { EMPTY, exhaustMap, mergeMap, of, switchMap } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { selectAccountCampaignsActive, selectAccountID, selectUserSocialMediaActive } from '../../../store/global/core-selectors';
import { HttpErrorResponse } from '@angular/common/http';
import { FeedbackService } from '../../../core/feedback.service';
import { MainRoutes } from '../../../core/enums';
import { NavigationService } from '../../../core/navigation.service';
import { selectCampaignFilters } from './campaign.selectors';
import { TranslateService } from '@ngx-translate/core';
import { updateSelectedAccount } from '../../../store/global/core-actions';
import { SortType } from '../../../models/campaigns-models/campaign-filters';
import { selectCurrentRoute } from '../../../store/global/router-selectors';


@Injectable()
export class campaignEffects {
  constructor(private actions$: Actions,
              private store: Store,
              private apiService: ApiService,
              private feedbackService: FeedbackService,
              private navigationService: NavigationService,
              private translate: TranslateService) {
  }

  getCampaigns$ = createEffect(() => {
    return this.actions$.pipe(
        // do NOT put updateTitleFilter action in here as that should not trigger a BE call
        ofType(CampaignsActions.getCampaigns,
            CampaignsActions.updateStatusFilter,
            CampaignsActions.updateTypeFilter,
            CampaignsActions.updateSortType,
            CampaignsActions.updateDateRange,
            CampaignsActions.updateGbpAccountIds,
            CampaignsActions.updateAllFilters),
        concatLatestFrom(() => [
          this.store.select(selectAccountID),
          this.store.select(selectCampaignFilters),
          this.store.select(selectAccountCampaignsActive),
          this.store.select(selectCurrentRoute),
        ]),
        exhaustMap(([action, accountId, campaignFilters, campaignsActive, currentRoute]) => {
          // Don't fetch anything if Campaigns feature is disabled
          return campaignsActive && currentRoute?.includes(MainRoutes.POSTS) ?
              this.apiService.getCampaigns(accountId, campaignFilters).pipe(
                  map((res) => {
                    return CampaignsActions.getCampaignsSuccess({ campaignsResponse: res?.result });
                  }),
                  catchError((err: HttpErrorResponse) =>
                      of(CampaignsActions.getCampaignsError({
                        error: err,
                        userFriendlyMessage: this.translate.instant('CAMPAIGNS.ERRORS.CAMPAIGNS_LOAD_FAIL'),
                      })),
                  ),
              ) : EMPTY;
        }),
    );
  });


  upsertCampaign$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CampaignsActions.upsertCampaign),
        concatLatestFrom(() => [this.store.select(selectAccountID)]),
        mergeMap(([action, accountId]) =>
            this.apiService.upsertCampaign(accountId, action.campaignPayload, action.files, action.id).pipe(
                mergeMap((res) => {
                  this.feedbackService.showSuccessSnackbar({
                    data: {
                      userFriendlyMessage: this.translate.instant(action.id ?
                          'CAMPAIGNS.CAMPAIGN_EDIT_SUCCESS' :
                          'CAMPAIGNS.CAMPAIGN_CREATE_SUCCESS'),
                    },
                  });
                  return [
                    CampaignsActions.upsertCampaignSuccess({ campaign: res?.result }),
                    CampaignsActions.getClientTokens(), // refresh available tokens
                  ];
                }),
                catchError((err: HttpErrorResponse) =>
                    of(CampaignsActions.upsertCampaignError({
                      error: err,
                      userFriendlyMessage: this.translate.instant(action.id ?
                          'CAMPAIGNS.ERRORS.CAMPAIGN_EDIT_FAIL' :
                          'CAMPAIGNS.ERRORS.CAMPAIGN_CREATE_FAIL'),
                    })),
                ),
            ),
        ),
    );
  });

  deleteCampaign$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CampaignsActions.deleteCampaign),
        concatLatestFrom(() => [this.store.select(selectAccountID)]),
        mergeMap(([{ campaignId }, accountId]) =>
            this.apiService.deleteCampaign(accountId, campaignId).pipe(
                map((res) => {
                  this.feedbackService.showSuccessSnackbar({
                    data: {
                      userFriendlyMessage: this.translate.instant('CAMPAIGNS.CAMPAIGN_DELETE_SUCCESS'),
                    },
                  });
                  this.navigationService.navigate([MainRoutes.POSTS]).then();
                  return CampaignsActions.deleteCampaignSuccess({ campaignId });
                }),
                catchError((err: HttpErrorResponse) =>
                    of(CampaignsActions.deleteCampaignError({
                      error: err,
                      userFriendlyMessage: this.translate.instant('CAMPAIGNS.ERRORS.CAMPAIGN_DELETE_FAIL'),
                      campaignId,
                    })),
                ),
            ),
        ),
    );
  });


  archiveCampaign$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CampaignsActions.archiveCampaign),
        concatLatestFrom(() => [this.store.select(selectAccountID)]),
        mergeMap(([{ campaignId }, accountId]) =>
            this.apiService.archiveCampaign(accountId, campaignId).pipe(
                map((res) => {
                  if (res && res.result) {
                    this.feedbackService.showSuccessSnackbar({
                      data: {
                        userFriendlyMessage: this.translate.instant('CAMPAIGNS.CAMPAIGN_ARCHIVE_SUCCESS'),
                      },
                    });
                    this.navigationService.navigate([MainRoutes.POSTS]).then();
                    return CampaignsActions.archiveCampaignSuccess({ campaign: res.result });
                  }
                }),
                catchError((err: HttpErrorResponse) =>
                    of(CampaignsActions.archiveCampaignError({
                      error: err,
                      userFriendlyMessage: this.translate.instant('CAMPAIGNS.ERRORS.CAMPAIGN_ARCHIVE_FAIL'),
                      campaignId,
                    })),
                ),
            ),
        ),
    );
  });

  getClientTokens$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CampaignsActions.getClientTokens),
        concatLatestFrom(() => [this.store.select(selectAccountID),
          this.store.select(selectAccountCampaignsActive),
          this.store.select(selectCurrentRoute)]),
        switchMap(([_, accountId, campaignsActive, currentRoute]) =>
            campaignsActive && currentRoute?.includes(MainRoutes.POSTS) ?
                this.apiService.getClientTokens(accountId).pipe(
                    map((res) => {
                      if (res && res.result) {
                        return CampaignsActions.getClientTokensSuccess({ clientTokens: res.result });
                      }
                    }),
                    catchError((err: HttpErrorResponse) =>
                        of(CampaignsActions.getClientTokensError({
                          error: err,
                          userFriendlyMessage: this.translate.instant('CAMPAIGNS.ERRORS.TOKENS_LOAD_FAIL'),
                        })),
                    ),
                ) : EMPTY,
        ),
    );
  });

  setClientTokens$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CampaignsActions.setClientTokens),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => this.apiService.setClientTokens(accountId, action.tokensToAdd)
            .pipe(
                map((res) => {
                  if (res && res.result) {
                    return CampaignsActions.setClientTokensSuccess({ clientTokens: res.result });
                  }
                }),
                catchError((err: HttpErrorResponse) =>
                    of(CampaignsActions.setClientTokensError({
                      error: err,
                      userFriendlyMessage: this.translate.instant('CAMPAIGNS.ERRORS.TOKENS_SET_FAIL'),
                    })),
                ),
            ),
        ),
    );
  });

  getPlatforms$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(MediaPlatformActions.getPlatforms),
        concatLatestFrom(() => [this.store.select(selectAccountID),
          this.store.select(selectAccountCampaignsActive),
          this.store.select(selectUserSocialMediaActive),
          this.store.select(selectCurrentRoute)]),
        switchMap(([a, accountId, campaignsActive, smActive, currentRoute]) =>
            campaignsActive && smActive && currentRoute?.includes(MainRoutes.POSTS) ? this.apiService.getPlatforms(accountId)
                .pipe(
                    map((res) => {
                      if (res.result) {
                        return MediaPlatformActions.getPlatformsSuccess({ platforms: res.result });
                      }
                    }),
                    catchError((err: HttpErrorResponse) =>
                        of(MediaPlatformActions.getPlatformsFail({
                          error: err,
                          userFriendlyMessage: this.translate.instant('ERROR'),
                        })),
                    ),
                ) : EMPTY,
        ),
    );
  });

  connectPlatform$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(MediaPlatformActions.connectPlatform),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([a, accountId]) =>
            this.apiService.connectPlatform(accountId, a.platform, a.frontendRedirectUrl)
                .pipe(
                    map((res) => {
                      if (res?.result?.redirectUrl) {
                        window.location.assign(res.result.redirectUrl);
                        return MediaPlatformActions.connectPlatformSuccess();
                      }
                    }),
                    catchError((err: HttpErrorResponse) =>
                        of(MediaPlatformActions.connectPlatformFail({
                          error: err,
                          userFriendlyMessage: this.translate.instant('ERROR'),
                        })),
                    ),
                ),
        ),
    );
  });

  disconnectPlatform$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(MediaPlatformActions.disconnectPlatform),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([a, accountId]) =>
            this.apiService.disconnectPlatform(a.id, accountId)
                .pipe(
                    map((res) => {
                      if (res.result) {
                        return MediaPlatformActions.disconnectPlatformSuccess({ platform: res.result });
                      }
                    }),
                    catchError((err: HttpErrorResponse) =>
                        of(MediaPlatformActions.disconnectPlatformFail({
                          error: err,
                          userFriendlyMessage: this.translate.instant('ERROR'),
                        })),
                    ),
                ),
        ),
    );
  });

  getValidationRules$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CampaignsActions.getValidationRules),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([_, accountId]) =>
            this.apiService.getValidationRules(accountId).pipe(
                map((res) => {
                  if (res && res.result) {
                    return CampaignsActions.getValidationRulesSuccess({ validationRules: res.result });
                  }
                }),
                catchError((err: HttpErrorResponse) =>
                    of(CampaignsActions.getValidationRulesError({
                      error: err,
                      userFriendlyMessage: this.translate.instant('CAMPAIGNS.ERRORS.VALIDATION_RULES_LOAD_FAIL'),
                    })),
                ),
            ),
        ),
    );
  });

  apiCallError$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CampaignsActions.upsertCampaignError,
            CampaignsActions.getCampaignsError,
            CampaignsActions.deleteCampaignError,
            CampaignsActions.archiveCampaignError,
            CampaignsActions.getClientTokensError,
            CampaignsActions.setClientTokensError,
            CampaignsActions.getValidationRulesError,
            MediaPlatformActions.getPlatformsFail,
            MediaPlatformActions.connectPlatformFail,
            MediaPlatformActions.disconnectPlatformFail),
        tap((action) => {
          const message: string = action.error?.error?.message ?
              action.error?.error?.message :
              action.error?.message;

          this.feedbackService.showAlertSnackbar({
            data: {
              userFriendlyMessage: action.userFriendlyMessage,
              detailedInfoFromBE: message?.split(';'),
            },
          }, action.error?.status);
          console.error(action.error);
        }),
    );
  }, { dispatch: false });


  reloadDataOnAccountSwitch$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(updateSelectedAccount),
        mergeMap(() => [
          // clear filters (which causes to refresh campaign list)
          CampaignsActions.updateAllFilters({
            filters: {
              title: '',
              type: null,
              status: null,
              from: null,
              to: null,
              sortType: SortType.NEWEST,
              gbpAccountIds: [],
              platform: [],
            },
          }),
          CampaignsActions.getClientTokens(),
          MediaPlatformActions.getPlatforms(),
          CampaignsActions.getValidationRules(),
        ]),
    );
  });

}
